import * as React from 'react';
import { connect } from 'react-redux';
import { withStyles, WithStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import * as classNames from 'classnames';
import style from './GeotreeInfoPanel.style';
import { FMContainer, FMToolbar, FMButton, FMCheckbox, FMTextInput } from 'atoms';
import { FMIconPicker } from 'molecules';
import { FMColorPicker, FMDropDownTree } from 'organisms';
import EmptyPanel from './EmptyPanel';
import { getNodeEditData, getSelectedNode, getInfoPanelFetching } from '../../../store/FMTree/FMTReeSelectors';
import { disableEditMode, updateEditNodeParam, saveGeotreeEditNode } from '../../../store/FMTree/FMTreeActions';
import {
    SelectedTreeNode,
    NodeEditData,
    TreeDisplayStateType,
    NodeParam,
    ParamType,
    TreeType,
    ParamSysName,
    TreeNodeType,
} from '../../../store/FMTree/FMTreeReducer';
import { RootState } from '../../../store';
import { IconData, IconType } from '../../../store/FMImages/FMImagesReducer';
import { getIconsData } from '../../../store/FMImages/FMImagesSelectors';
import { FMLoading } from 'molecules';

interface StateProps {
    data: NodeEditData;
    selectedNode: SelectedTreeNode;
    iconsData: IconData[];
    infoPanelFetching: boolean;
}

interface DispatchProps {
    disableEditMode: (displayState: TreeDisplayStateType) => void;
    updateEditNodeParam: (displayState: TreeDisplayStateType, sysName: string, value: any) => void;
    saveGeotreeEditNode: (displayState: TreeDisplayStateType) => void;
}

type Props = StateProps & DispatchProps & WithStyles;

const EditPanel = (props: Props) => {
    if (props.data === null) {
        return <EmptyPanel />;
    }

    let disableEditModeInner = () => props.disableEditMode(TreeDisplayStateType.mainGeofencesTree);
    let updateParamValue = (sysName, value) =>
        props.updateEditNodeParam(TreeDisplayStateType.mainGeofencesTree, sysName, value);
    let saveNode = () => props.saveGeotreeEditNode(TreeDisplayStateType.mainGeofencesTree);

    return (
        <FMContainer className={props.classes.panel}>
            {
                props.infoPanelFetching ?
                <FMLoading />
                :
                <React.Fragment>
                    {
                        props.data.params.map(param =>
                            <div key={param.sysName} className={props.classes.paramEditContainer}>
                                <Typography className={props.classes.paramEditLabel}>{param.label}:</Typography>
                                <div className={classNames(
                                        props.classes.paramEditInputContainer,
                                        param.type !== ParamType.check && props.classes.paramEditInputContainerNotCheck
                                )}>
                                    {
                                        getParamInput(
                                            param,
                                            updateParamValue,
                                            props.iconsData,
                                            props.data.nodeType === TreeNodeType.group,
                                        )
                                    }
                                </div>
                            </div>
                        )
                    }
                     <FMToolbar variant="dense">
                        <FMButton variant="outlined" color="primary" onClick={saveNode}>
                            {fmtools.translate('Save')}
                        </FMButton>
                        <FMButton variant="outlined" onClick={disableEditModeInner}>
                            {fmtools.translate('Cancel')}
                        </FMButton>
                    </FMToolbar>
                </React.Fragment>
            }
        </FMContainer>
    );
};

const getParamInput = (param: NodeParam, updateParam, iconsData: IconData[], isGroup: boolean) => {
    if (!param.editable) {
        return <Typography>{param.value.toString()}</Typography>;
    }

    switch (param.type) {
        case ParamType.number: return <FMTextInput
                value={param.value.toString()}
                onChange={(event: any) => updateParam(param.sysName, event.target.value)}
                type="number"
            />;
        case ParamType.text: return <FMTextInput
                value={param.value.toString()}
                onChange={(event: any) => updateParam(param.sysName, event.target.value)}
                multiline={param.sysName === ParamSysName.description}
            />;
        case ParamType.check: return <FMCheckbox
                checked={param.value}
                onClick={(event: any) => updateParam(param.sysName, event.target.checked)}
            />;
        case ParamType.icon: return <FMIconPicker
                iconsPath="../../../img/"
                icons={iconsData}
                initialValue={param.value}
                onChange={(value: string) => updateParam(param.sysName, value)}
            />;
        case ParamType.color: return <FMColorPicker
                initialColor={param.value}
                onChange={(value: string) => updateParam(param.sysName, value)}
            />;
        case ParamType.parentGroup: return <FMDropDownTree
                treeType={TreeType.geofencesTree}
                treeDisplayState={TreeDisplayStateType.dropDownGeofencesTree}
                onChange={(value: string) => updateParam(param.sysName, value)}
                initialValue={param.value}
                showRoot={isGroup}
            />;
        default: return <Typography>{param.value.toString()}</Typography>;
    }
};

const mapStateToProps = (state: RootState): StateProps => ({
    data: getNodeEditData(state, TreeDisplayStateType.mainGeofencesTree),
    selectedNode: getSelectedNode(state, TreeDisplayStateType.mainGeofencesTree),
    iconsData: getIconsData(state, IconType.geoPoint),
    infoPanelFetching: getInfoPanelFetching(state, TreeDisplayStateType.mainGeofencesTree)
});

const mapDispatchToProps = {
    disableEditMode,
    updateEditNodeParam,
    saveGeotreeEditNode,
};

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(style)(EditPanel));
